export interface CompanyTokenAllocationSummary {
  allocatedTokens: number;
  usedTokens: number;
  remainingTokens: number;
}

export interface UserTokenQuota {
  /** Encrypted public quota row id. */
  id: string;
  userName: string;
  userEmail: string;
  allocatedTokens: number;
  usedTokens: number;
  remainingTokens: number;
  renewalTokens: number;
  unauthorizedPromptAttempts: number;
  /** `1` when the allocation was soft-deleted (`is_delete`). */
  isDelete?: 0 | 1;
  allocationDate: string;
  /** `0` One Time, `1` Weekly, `2` Monthly. */
  renewalType: UserQuotaRenewalType;
  /** Weekly: `0`–`6` (Sun–Sat). Monthly: `1`–`28`, `31` = last day. */
  renewalOn: number | null;
  /** `1` enabled, `0` disabled. */
  renewalStatus: 0 | 1;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedOn: string;
}

export interface AssignableUserOption {
  /** Encrypted public user id — sent on create only. */
  id: string;
  userName: string;
  userEmail: string;
}

export interface UserTokenQuotaListResponse {
  items: UserTokenQuota[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export type UserQuotaRemainingFilter = 'all' | 'has_remaining' | 'exhausted';

export type UserQuotaSortField =
  | 'userName'
  | 'allocatedTokens'
  | 'usedTokens'
  | 'remainingTokens'
  | 'allocationDate'
  | 'createdAt'
  | 'updatedAt'
  | 'updatedOn';

export type UserQuotaListStatus = 'active' | 'deleted';

export interface ListUserTokenQuotasQuery {
  page?: number;
  limit?: number;
  q?: string;
  remaining?: UserQuotaRemainingFilter;
  sortBy?: UserQuotaSortField;
  sortDir?: 'asc' | 'desc';
  status?: UserQuotaListStatus;
}

export interface CreateUserTokenQuotaDto {
  userId: string;
  allocatedTokens: number;
  renewalType: UserQuotaRenewalType;
  renewalOn?: number | null;
  renewalStatus: 0 | 1;
}

export interface UpdateUserTokenQuotaDto {
  allocatedTokens: number;
  renewalType: UserQuotaRenewalType;
  renewalOn?: number | null;
  renewalStatus: 0 | 1;
  renewalTokens: number;
}

export interface AddUserTokenQuotaTokensDto {
  addTokens: number;
}

export interface TokenAllocationActivity {
  id: string;
  description: string;
  createdAt: string;
}

export interface TokenAllocationActivityListResponse {
  items: TokenAllocationActivity[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface ListTokenAllocationActivitiesQuery {
  page?: number;
  limit?: number;
  q?: string;
}

export const TOKEN_ALLOCATION_MIN_SEARCH_LENGTH = 3;

/** `0` One Time, `1` Weekly, `2` Monthly — matches `agentic_ai_user_quotas.renewal_type`. */
export type UserQuotaRenewalType = 0 | 1 | 2;

export interface UserQuotaRenewalTypeOption {
  label: string;
  value: UserQuotaRenewalType;
}

export interface UserQuotaRenewalOnOption {
  label: string;
  value: number;
}

export const USER_QUOTA_RENEWAL_TYPE_OPTIONS: UserQuotaRenewalTypeOption[] = [
  { label: 'One Time', value: 0 },
  { label: 'Weekly', value: 1 },
  { label: 'Monthly', value: 2 },
];

/** JavaScript `Date.getDay()` — `0` Sunday … `6` Saturday. */
export const USER_QUOTA_WEEKLY_RENEWAL_ON_OPTIONS: UserQuotaRenewalOnOption[] = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
];

/** Latest fixed calendar day for monthly renewal (all months include 1–28). */
export const USER_QUOTA_MONTHLY_RENEWAL_ON_MAX_DAY = 28;

/** Monthly `renewal_on` sentinel — last calendar day of the month. */
export const USER_QUOTA_MONTHLY_RENEWAL_ON_LAST_DAY = 31;

export function isValidMonthlyRenewalOn(day: number): boolean {
  const n = Math.floor(Number(day));
  return (n >= 1 && n <= USER_QUOTA_MONTHLY_RENEWAL_ON_MAX_DAY) || n === USER_QUOTA_MONTHLY_RENEWAL_ON_LAST_DAY;
}

export const USER_QUOTA_MONTHLY_RENEWAL_ON_OPTIONS: UserQuotaRenewalOnOption[] = [
  ...Array.from({ length: USER_QUOTA_MONTHLY_RENEWAL_ON_MAX_DAY }, (_, index) => ({
    label: String(index + 1),
    value: index + 1,
  })),
  { label: 'Last day of month', value: USER_QUOTA_MONTHLY_RENEWAL_ON_LAST_DAY },
];

export function parseUserQuotaRenewalType(raw: unknown): UserQuotaRenewalType {
  const n = Number(raw);
  if (n === 1 || n === 2) {
    return n;
  }
  return 0;
}

export function parseUserQuotaRenewalOn(
  raw: unknown,
  renewalType: UserQuotaRenewalType,
): number | null {
  if (renewalType === 0) {
    return null;
  }
  if (raw == null || raw === '') {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return null;
  }
  if (renewalType === 1) {
    return Math.max(0, Math.min(6, Math.floor(n)));
  }
  if (renewalType === 2) {
    const day = Math.floor(n);
    if (isValidMonthlyRenewalOn(day)) {
      return day;
    }
    return null;
  }
  return null;
}

export function parseUserQuotaRenewalStatus(raw: unknown): 0 | 1 {
  const n = Number(raw);
  return n === 1 ? 1 : 0;
}

export function userQuotaRenewalTypeLabel(renewalType: UserQuotaRenewalType): string {
  return USER_QUOTA_RENEWAL_TYPE_OPTIONS.find((o) => o.value === renewalType)?.label ?? 'One Time';
}

export function userQuotaRenewalOnLabel(
  renewalType: UserQuotaRenewalType,
  renewalOn: number | null,
): string {
  if (renewalType === 0 || renewalOn == null) {
    return '—';
  }
  if (renewalType === 1) {
    return (
      USER_QUOTA_WEEKLY_RENEWAL_ON_OPTIONS.find((o) => o.value === renewalOn)?.label ?? '—'
    );
  }
  if (renewalType === 2) {
    return (
      USER_QUOTA_MONTHLY_RENEWAL_ON_OPTIONS.find((o) => o.value === renewalOn)?.label ?? '—'
    );
  }
  return '—';
}

export function userQuotaRenewalOnOptions(
  renewalType: UserQuotaRenewalType,
): UserQuotaRenewalOnOption[] {
  if (renewalType === 1) {
    return USER_QUOTA_WEEKLY_RENEWAL_ON_OPTIONS;
  }
  if (renewalType === 2) {
    return USER_QUOTA_MONTHLY_RENEWAL_ON_OPTIONS;
  }
  return [];
}

export function defaultUserQuotaRenewalOn(renewalType: UserQuotaRenewalType): number | null {
  if (renewalType === 1) {
    return 0;
  }
  if (renewalType === 2) {
    return 1;
  }
  return null;
}

export interface TokenUsagePercentages {
  usedPercent: number;
  remainingPercent: number;
}

export type TokenUsageBarTone = 'green' | 'orange' | 'red';

/** Bar color from consumed %: &lt;35 green, 35–74 orange, ≥75 red. */
export function consumedPercentBarTone(consumedPercent: number): TokenUsageBarTone {
  const p = Math.max(0, Math.min(100, Math.round(Number(consumedPercent) || 0)));
  if (p >= 75) {
    return 'red';
  }
  if (p >= 35) {
    return 'orange';
  }
  return 'green';
}

/** Used vs remaining share of allocated tokens (0–100 each; sums to 100 when allocated > 0). */
export function computeTokenUsagePercentages(
  allocatedTokens: number,
  usedTokens: number,
): TokenUsagePercentages {
  const allocated = Math.max(0, Number(allocatedTokens) || 0);
  const used = Math.max(0, Number(usedTokens) || 0);
  if (allocated <= 0) {
    if (used > 0) {
      return { usedPercent: 100, remainingPercent: 0 };
    }
    return { usedPercent: 0, remainingPercent: 100 };
  }
  const usedPercent = Math.min(100, Math.round((used / allocated) * 100));
  return {
    usedPercent,
    remainingPercent: Math.max(0, 100 - usedPercent),
  };
}

/** True when allocated is strictly below used — invalid for quota updates. */
export function isAllocatedBelowUsedTokens(allocatedTokens: number, usedTokens: number): boolean {
  const allocated = Math.max(0, Math.floor(Number(allocatedTokens) || 0));
  const used = Math.max(0, Math.floor(Number(usedTokens) || 0));
  return allocated < used;
}

export function allocatedBelowUsedTokensMessage(usedTokens: number): string {
  const used = Math.max(0, Math.floor(Number(usedTokens) || 0));
  return `Allocated tokens cannot be less than used tokens (${used.toLocaleString()}).`;
}

export function normalizeUserTokenQuota(raw: Record<string, unknown>): UserTokenQuota {
  const allocatedTokens = Math.max(0, Number(raw['allocatedTokens'] ?? raw['allocated_tokens'] ?? 0) || 0);
  const usedTokens = Math.max(0, Number(raw['usedTokens'] ?? raw['used_tokens'] ?? 0) || 0);
  const remainingRaw = raw['remainingTokens'] ?? raw['remaining_tokens'];
  const remainingTokens =
    remainingRaw != null
      ? Math.max(0, Number(remainingRaw) || 0)
      : Math.max(0, allocatedTokens - usedTokens);
  const idRaw = raw['id'];
  const allocationDateRaw = raw['allocationDate'] ?? raw['allocation_date'];
  const createdAtRaw = raw['createdAt'] ?? raw['created_at'];
  const createdByRaw = raw['createdBy'] ?? raw['created_by_name'] ?? raw['created_by'];
  const updatedAtRaw = raw['updatedAt'] ?? raw['updated_at'];
  const updatedOnRaw = raw['updatedOn'] ?? raw['updated_on'];
  const renewalTypeRaw = raw['renewalType'] ?? raw['renewal_type'];
  const renewalOnRaw = raw['renewalOn'] ?? raw['renewal_on'];
  const renewalStatusRaw = raw['renewalStatus'] ?? raw['renewal_status'];
  const renewalTokens = Math.max(
    0,
    Number(raw['renewalTokens'] ?? raw['renewal_tokens'] ?? 0) || 0,
  );
  const renewalType = parseUserQuotaRenewalType(renewalTypeRaw);
  return {
    id: typeof idRaw === 'string' ? idRaw.trim() : String(idRaw ?? ''),
    userName: String(raw['userName'] ?? raw['user_name'] ?? '').trim(),
    userEmail: String(raw['userEmail'] ?? raw['user_email'] ?? '').trim(),
    allocatedTokens,
    usedTokens,
    remainingTokens,
    renewalTokens,
    unauthorizedPromptAttempts: Math.max(
      0,
      Number(raw['unauthorizedPromptAttempts'] ?? raw['unauthorized_prompt_attempts'] ?? 0) || 0,
    ),
    isDelete:
      raw['isDelete'] === 1 ||
      raw['isDelete'] === '1' ||
      raw['is_delete'] === 1 ||
      raw['is_delete'] === '1'
        ? 1
        : 0,
    allocationDate: typeof allocationDateRaw === 'string' ? allocationDateRaw.trim() : '',
    renewalType,
    renewalOn: parseUserQuotaRenewalOn(renewalOnRaw, renewalType),
    renewalStatus: parseUserQuotaRenewalStatus(renewalStatusRaw),
    createdAt: typeof createdAtRaw === 'string' ? createdAtRaw.trim() : '',
    createdBy:
      typeof createdByRaw === 'string'
        ? createdByRaw.trim()
        : typeof createdByRaw === 'number' && createdByRaw > 0
          ? `User #${createdByRaw}`
          : '',
    updatedAt: typeof updatedAtRaw === 'string' ? updatedAtRaw.trim() : '',
    updatedOn: typeof updatedOnRaw === 'string' ? updatedOnRaw.trim() : '',
  };
}

export function normalizeAssignableUser(raw: Record<string, unknown>): AssignableUserOption {
  const idRaw = raw['id'];
  return {
    id: typeof idRaw === 'string' ? idRaw.trim() : String(idRaw ?? ''),
    userName: String(raw['userName'] ?? raw['user_name'] ?? '').trim(),
    userEmail: String(raw['userEmail'] ?? raw['user_email'] ?? '').trim(),
  };
}

export function normalizeTokenAllocationActivity(raw: Record<string, unknown>): TokenAllocationActivity {
  const idRaw = raw['id'];
  const createdRaw = raw['createdAt'] ?? raw['created_at'] ?? raw['created'];
  return {
    id: typeof idRaw === 'string' ? idRaw.trim() : String(idRaw ?? ''),
    description: String(raw['description'] ?? '').trim(),
    createdAt: typeof createdRaw === 'string' ? createdRaw.trim() : '',
  };
}

export function normalizeCompanyTokenSummary(raw: Record<string, unknown>): CompanyTokenAllocationSummary {
  const allocatedTokens = Math.max(0, Number(raw['allocatedTokens'] ?? raw['allocated_tokens'] ?? 0) || 0);
  const usedTokens = Math.max(0, Number(raw['usedTokens'] ?? raw['used_tokens'] ?? 0) || 0);
  const remainingRaw = raw['remainingTokens'] ?? raw['remaining_tokens'];
  const remainingTokens =
    remainingRaw != null
      ? Math.max(0, Number(remainingRaw) || 0)
      : Math.max(0, allocatedTokens - usedTokens);
  return { allocatedTokens, usedTokens, remainingTokens };
}

export function assignableUserLabel(user: AssignableUserOption): string {
  const name = user.userName.trim();
  const email = user.userEmail.trim();
  if (name && email) {
    return `${name} (${email})`;
  }
  return name || email || 'User';
}

export type UserQuotaUsagePeriod =
  | 'today'
  | 'this_week'
  | 'this_month'
  | 'last_6_months'
  | 'this_year'
  | 'all_time';

export interface UserQuotaUsagePeriodOption {
  label: string;
  value: UserQuotaUsagePeriod;
}

export const USER_QUOTA_USAGE_PERIOD_OPTIONS: UserQuotaUsagePeriodOption[] = [
  { label: 'Today', value: 'today' },
  { label: 'This week', value: 'this_week' },
  { label: 'This month', value: 'this_month' },
  { label: 'Last 6 months', value: 'last_6_months' },
  { label: 'This year', value: 'this_year' },
  { label: 'All time', value: 'all_time' },
];

export interface UserQuotaAgentUsage {
  agentCode: string;
  agentName: string;
  tokens: number;
  percent: number;
}

export interface UserQuotaUsageMetrics {
  /** `company_database` — never BigQuery. */
  dataSource?: 'company_database';
  period: UserQuotaUsagePeriod;
  dateFrom: string | null;
  dateTo: string;
  totalTokens: number;
  byAgent: UserQuotaAgentUsage[];
}

export const USER_QUOTA_AGENT_CHART_COLORS: Record<string, string> = {
  ask_anything: '#2563eb',
  resume_ranker: '#7c3aed',
  email_agent: '#059669',
  submission_agent: '#d97706',
  pipeline_agent: '#0891b2',
  notes_agent: '#db2777',
  task_manager: '#4f46e5',
  interview_scheduling_agent: '#ea580c',
  voice_ai_call_agent: '#0d9488',
};

export function normalizeUserQuotaUsageMetrics(raw: Record<string, unknown>): UserQuotaUsageMetrics {
  const periodRaw = raw['period'];
  const period =
    typeof periodRaw === 'string' &&
    USER_QUOTA_USAGE_PERIOD_OPTIONS.some((o) => o.value === periodRaw)
      ? (periodRaw as UserQuotaUsagePeriod)
      : 'this_month';
  const byAgentRaw = Array.isArray(raw['byAgent']) ? raw['byAgent'] : raw['by_agent'];
  const byAgent = (Array.isArray(byAgentRaw) ? byAgentRaw : []).map((row) => {
    const item = row as Record<string, unknown>;
    const agentCode = String(item['agentCode'] ?? item['agent_code'] ?? '').trim();
    return {
      agentCode,
      agentName: String(item['agentName'] ?? item['agent_name'] ?? agentCode).trim(),
      tokens: Math.max(0, Number(item['tokens'] ?? 0) || 0),
      percent: Math.max(0, Number(item['percent'] ?? 0) || 0),
    };
  });
  const dateFromRaw = raw['dateFrom'] ?? raw['date_from'];
  const dateToRaw = raw['dateTo'] ?? raw['date_to'];
  return {
    dataSource: 'company_database' as const,
    period,
    dateFrom: typeof dateFromRaw === 'string' ? dateFromRaw.trim() : null,
    dateTo: typeof dateToRaw === 'string' ? dateToRaw.trim() : '',
    totalTokens: Math.max(0, Number(raw['totalTokens'] ?? raw['total_tokens'] ?? 0) || 0),
    byAgent,
  };
}

export interface UsageSummaryDailyPoint {
  date: string;
  byAgent: Record<string, number>;
  totalTokens: number;
}

export interface UsageSummaryTopUser {
  userId: number;
  userName: string;
  userEmail: string;
  totalTokens: number;
  topAgentCode: string;
  topAgentName: string;
  topAgentTokens: number;
}

export interface UsageSummaryFilterUser {
  userId: number;
  userName: string;
  userEmail: string;
}

export interface UsageSummary {
  dataSource?: 'company_database';
  period: UserQuotaUsagePeriod;
  dateFrom: string | null;
  dateTo: string;
  totalTokens: number;
  byAgent: UserQuotaAgentUsage[];
  dailyByAgent: UsageSummaryDailyPoint[];
  topUsers: UsageSummaryTopUser[];
  filterUsers: UsageSummaryFilterUser[];
  scopeUserId: number | null;
  agentFilter: string | null;
  isAdministrator: boolean;
}

export const USAGE_SUMMARY_AGENT_FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: 'All agents', value: '' },
  { label: 'Ask anything', value: 'ask_anything' },
  { label: 'Search and match', value: 'resume_ranker' },
  { label: 'Email agent', value: 'email_agent' },
  { label: 'Pipeline agent', value: 'pipeline_agent' },
  { label: 'Submission agent', value: 'submission_agent' },
  { label: 'Interview Schedule', value: 'interview_scheduling_agent' },
  { label: 'Voice AI Interview', value: 'voice_ai_call_agent' },
  { label: 'Task Agent', value: 'task_manager' },
  { label: 'Notes agent', value: 'notes_agent' },
];

export const USAGE_SUMMARY_AGENT_MULTI_OPTIONS = USAGE_SUMMARY_AGENT_FILTER_OPTIONS.filter(
  (option) => option.value !== '',
);

export function normalizeUsageSummary(raw: Record<string, unknown>): UsageSummary {
  const metrics = normalizeUserQuotaUsageMetrics(raw);
  const dailyRaw = Array.isArray(raw['dailyByAgent'])
    ? raw['dailyByAgent']
    : raw['daily_by_agent'];
  const dailyByAgent = (Array.isArray(dailyRaw) ? dailyRaw : []).map((row) => {
    const item = row as Record<string, unknown>;
    const byAgentRaw = item['byAgent'] ?? item['by_agent'];
    const byAgent: Record<string, number> = {};
    if (byAgentRaw != null && typeof byAgentRaw === 'object' && !Array.isArray(byAgentRaw)) {
      for (const [key, val] of Object.entries(byAgentRaw as Record<string, unknown>)) {
        byAgent[String(key)] = Math.max(0, Number(val) || 0);
      }
    }
    return {
      date: String(item['date'] ?? '').trim(),
      byAgent,
      totalTokens: Math.max(0, Number(item['totalTokens'] ?? item['total_tokens'] ?? 0) || 0),
    };
  });
  const topRaw = Array.isArray(raw['topUsers']) ? raw['topUsers'] : raw['top_users'];
  const topUsers = (Array.isArray(topRaw) ? topRaw : []).map((row) => {
    const item = row as Record<string, unknown>;
    const topAgentCode = String(item['topAgentCode'] ?? item['top_agent_code'] ?? '').trim();
    return {
      userId: Math.max(0, Number(item['userId'] ?? item['user_id'] ?? 0) || 0),
      userName: String(item['userName'] ?? item['user_name'] ?? '').trim(),
      userEmail: String(item['userEmail'] ?? item['user_email'] ?? '').trim(),
      totalTokens: Math.max(0, Number(item['totalTokens'] ?? item['total_tokens'] ?? 0) || 0),
      topAgentCode,
      topAgentName: String(
        item['topAgentName'] ?? item['top_agent_name'] ?? topAgentCode,
      ).trim(),
      topAgentTokens: Math.max(
        0,
        Number(item['topAgentTokens'] ?? item['top_agent_tokens'] ?? 0) || 0,
      ),
    };
  });
  const filterRaw = Array.isArray(raw['filterUsers'])
    ? raw['filterUsers']
    : raw['filter_users'];
  const filterUsers = (Array.isArray(filterRaw) ? filterRaw : []).map((row) => {
    const item = row as Record<string, unknown>;
    return {
      userId: Math.max(0, Number(item['userId'] ?? item['user_id'] ?? 0) || 0),
      userName: String(item['userName'] ?? item['user_name'] ?? '').trim(),
      userEmail: String(item['userEmail'] ?? item['user_email'] ?? '').trim(),
    };
  }).filter((row) => row.userId > 0);
  const scopeRaw = raw['scopeUserId'] ?? raw['scope_user_id'];
  const scopeUserId =
    scopeRaw == null || scopeRaw === ''
      ? null
      : Math.max(0, Number(scopeRaw) || 0) || null;
  const agentFilterRaw = raw['agentFilter'] ?? raw['agent_filter'];
  const agentFilter =
    typeof agentFilterRaw === 'string' && agentFilterRaw.trim()
      ? agentFilterRaw.trim()
      : null;
  return {
    dataSource: 'company_database',
    period: metrics.period,
    dateFrom: metrics.dateFrom,
    dateTo: metrics.dateTo,
    totalTokens: metrics.totalTokens,
    byAgent: metrics.byAgent,
    dailyByAgent,
    topUsers,
    filterUsers,
    scopeUserId,
    agentFilter,
    isAdministrator: Boolean(raw['isAdministrator'] ?? raw['is_administrator']),
  };
}

export type UsageDonutSegment = {
  agentCode: string;
  agentName: string;
  tokens: number;
  color: string;
  dashArray: string;
  dashOffset: number;
};

const USAGE_DONUT_RADIUS = 40;
const USAGE_DONUT_CIRCUMFERENCE = 2 * Math.PI * USAGE_DONUT_RADIUS;

export function buildUsageDonutSegments(
  byAgent: UserQuotaAgentUsage[],
  totalTokens: number,
  colorForAgent: (agentCode: string) => string,
): UsageDonutSegment[] {
  const total = Math.max(0, Number(totalTokens) || 0);
  if (total <= 0) {
    return [];
  }
  let offset = 0;
  const segments: UsageDonutSegment[] = [];
  for (const agent of byAgent) {
    if (agent.tokens <= 0) {
      continue;
    }
    const length = (agent.tokens / total) * USAGE_DONUT_CIRCUMFERENCE;
    segments.push({
      agentCode: agent.agentCode,
      agentName: agent.agentName,
      tokens: agent.tokens,
      color: colorForAgent(agent.agentCode),
      dashArray: `${length} ${USAGE_DONUT_CIRCUMFERENCE}`,
      dashOffset: -offset,
    });
    offset += length;
  }
  return segments;
}

export function usageAgentBarWidth(tokens: number, totalTokens: number): number {
  const total = Math.max(0, Number(totalTokens) || 0);
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((Math.max(0, tokens) / total) * 100));
}

